import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MobileErrorBoundary } from "@/components/ui/mobile-error-boundary";
import { SearchBar } from "@/components/SearchBar";
import { useIsMobile } from "@/hooks/use-mobile";
import { Logo } from "@/components/header/Logo";
import { UserMenu } from "@/components/header/UserMenu";
import { MobileMenu } from "@/components/header/MobileMenu";
import { RegionSelector } from "@/components/header/RegionSelector";
import { useAuth } from "@/contexts/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { Gauge, Menu, Search, MapPin } from "lucide-react";
import { useVendorServiceCache } from "@/hooks/useVendorServiceCache";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useState } from "react";

interface DashboardMenuItem {
  view: string;
  label: string;
  icon: React.ReactNode;
  path: string;
}

interface ServiceMenuItem {
  view: string;
  label: string;
  icon: React.ComponentType<any>;
  visible: boolean;
}

interface HeaderProps {
  selectedRegion?: string;
  setSelectedRegion?: (value: string) => void;
  searchQuery?: string;
  setSearchQuery?: (value: string) => void;
  showSearchAndLocation?: boolean;
  // New props for admin and service dashboard context
  isAdminPage?: boolean;
  isServiceDashboard?: boolean;
  dashboardMenuItems?: DashboardMenuItem[];
  activeDashboardView?: string;
  onDashboardViewChange?: (view: string) => void;
  serviceMenuItems?: ServiceMenuItem[];
  onServiceViewChange?: (view: string) => void;
  activeServiceView?: string;
}

export const Header = React.memo(({
  selectedRegion = "nairobi",
  setSelectedRegion = () => {},
  searchQuery = "",
  setSearchQuery = () => {},
  showSearchAndLocation = false,
  isAdminPage = false,
  isServiceDashboard = false,
  dashboardMenuItems = [],
  activeDashboardView,
  onDashboardViewChange,
  serviceMenuItems,
  onServiceViewChange,
  activeServiceView,
}: HeaderProps) => {
  const {
    isAuthenticated,
    isAdmin,
    isVendor,
    displayName,
    signOut,
    isLoading
  } = useAuth();
  const { serviceInfo } = useVendorServiceCache();
  const isMobile = useIsMobile();
  const location = useLocation();
  const [mobileSearchQuery, setMobileSearchQuery] = useState("");
  
  const dashboardUrl = serviceInfo ? `/service/${serviceInfo.id}/calendar` : "/vendor-dashboard";

  const handleMobileSearch = () => {
    setSearchQuery(mobileSearchQuery);
  };

  return (
    <MobileErrorBoundary componentName="Header">
      <header className="fixed top-0 left-0 right-0 w-full bg-white/80 backdrop-blur-lg border-b border-gray-200/50 h-16 z-50">
      <div className="container h-full flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          {/* Admin Sidebar Trigger for mobile */}
          {isAdminPage && isMobile && (
            <SidebarTrigger />
          )}
          <div className="flex flex-col items-start">
            <Logo />
            {!isAuthenticated && (
              <span className="text-sm text-muted-foreground -mt-1 ml-1">
                Trusted local services
              </span>
            )}
          </div>
        </div>
        
        {isLoading ? (
          <div className="flex items-center gap-2">
            <Skeleton className="h-10 w-24" />
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {/* Mobile Search and Location Icons */}
            {isMobile && showSearchAndLocation && !isServiceDashboard && !isAdminPage && (
              <>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-10 w-10">
                      <Search className="h-5 w-5" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Search Services</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col gap-4">
                      <SearchBar 
                        value={mobileSearchQuery} 
                        onChange={setMobileSearchQuery} 
                      />
                      <Button onClick={handleMobileSearch} className="w-full">
                        Search
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
                
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-10 w-10">
                      <MapPin className="h-5 w-5" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <div className="p-3">
                      <RegionSelector 
                        selectedRegion={selectedRegion} 
                        setSelectedRegion={setSelectedRegion} 
                      />
                    </div>
                  </PopoverContent>
                </Popover>
              </>
            )}
            
            {isMobile ? (
              // Use UserMenu on mobile for service dashboard pages to show service navigation
              isServiceDashboard && isAuthenticated ? (
                <UserMenu 
                  displayName={displayName || "User"}
                  isAdmin={isAdmin}
                  isVendor={isVendor}
                  onSignOut={signOut}
                  serviceMenuItems={serviceMenuItems}
                  onServiceViewChange={onServiceViewChange}
                  activeServiceView={activeServiceView}
                />
              ) : (
                <MobileMenu 
                  isSignedIn={isAuthenticated}
                  displayName={displayName || "User"}
                  isAdmin={isAdmin}
                  isServiceOwner={isVendor}
                  onSignOut={signOut}
                  isOnDashboard={isServiceDashboard}
                  dashboardItems={dashboardMenuItems}
                  activeDashboardView={activeDashboardView}
                  onDashboardViewChange={onDashboardViewChange}
                />
              )
            ) : (
              isAuthenticated ? (
                <UserMenu 
                  displayName={displayName || "User"}
                  isAdmin={isAdmin}
                  isVendor={isVendor}
                  onSignOut={signOut}
                  serviceMenuItems={serviceMenuItems}
                  onServiceViewChange={onServiceViewChange}
                  activeServiceView={activeServiceView}
                />
              ) : (
                <Link to="/auth">
                  <Button variant="outline">Sign In</Button>
                </Link>
              )
            )}
          </div>
        )}
      </div>
      
      {showSearchAndLocation && !isServiceDashboard && !isAdminPage && !isMobile && (
        <div className="border-t border-gray-200/50 bg-white/90 backdrop-blur-sm">
          <div className="container py-2 px-4">
            <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} items-center gap-2`}>
              <div className={`${isMobile ? 'w-full' : 'flex-grow'}`}>
                <SearchBar value={searchQuery} onChange={setSearchQuery} />
              </div>
              
              <div className={`${isMobile ? 'w-full mt-2' : ''} flex items-center`}>
                <RegionSelector 
                  selectedRegion={selectedRegion} 
                  setSelectedRegion={setSelectedRegion} 
                />
              </div>
            </div>
          </div>
        </div>
      )}
      </header>
    </MobileErrorBoundary>
  );
});
